import React from 'react'
import { InputAdornment, Stack, TextField, Typography } from '@mui/material'
import {
  CampaignSnsRequirementSnsType,
  ReviewSection,
  ReviewTarget,
  FormatVersion,
  CampaignSnsRequirementSnsPostType,
} from '~/generated/graphql'
import { Controller, useFormContext } from 'react-hook-form'
import { SnsIcon } from '~/components/common/SnsIcon'
import { Campaign_SNS_LABEL } from '~/constants/snstype'
import { CAMPAIGN_FORM_TITLE } from '~/constants/campaign'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { z } from 'zod'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'

export const requestUrlsSchemaFields = {
  campaignSnsRequirements: z
    .array(
      z.object({
        snsType: z.nativeEnum(CampaignSnsRequirementSnsType),
        tagList: z.array(
          z.object({
            languageType: z.enum(['JAPANESE', 'ENGLISH', 'CHINESE']),
            tags: z.array(z.string().max(255, { message: 'タグリストは255文字以内で入力してください' })),
          }),
        ),
        requestUrl: z.string().max(255, { message: 'リクエストURLは255文字以内で入力してください' }),
        // データ保持用(V3では利用しない)
        snsPostTypeList: z.array(z.nativeEnum(CampaignSnsRequirementSnsPostType)),
      }),
    )
    .min(1, { message: '少なくとも1つのSNS要件が必要です' }),
}
export const requestUrlsSchema = z.object(requestUrlsSchemaFields)

type Props = CampaignInputComponentProps

export const RequestUrls = ({ description }: Props) => {
  const { control, watch } = useFormContext<z.input<typeof requestUrlsSchema>>()
  const campaignSnsRequirements = watch('campaignSnsRequirements')
  return (
    <CampaignInputComponent
      title={CAMPAIGN_FORM_TITLE.requestUrl}
      required={true}
      description={description}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.RequestUrl, ReviewTarget.LegalRequestUrl],
      }}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      {(() => {
        const isYoutube = campaignSnsRequirements.some((e) => e.snsType === CampaignSnsRequirementSnsType.Youtube)
        if (isYoutube) {
          return (
            <Controller
              name={`campaignSnsRequirements.0.requestUrl`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  aria-label="requestUrl"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  placeholder="https://toridori.co.jp/"
                  error={!!error}
                  helperText={<ValidationErrorMessage error={error?.message} />}
                  fullWidth
                  {...field}
                  inputRef={ref}
                />
              )}
            />
          )
        }

        return campaignSnsRequirements.map((e, index) => (
          <Stack key={e.snsType} gap={0.5}>
            <Stack spacing={0.5} direction="row" alignItems="center">
              <SnsIcon snsType={e.snsType} size={32} />
              <Typography fontWeight="bold">
                {e.snsType === CampaignSnsRequirementSnsType.NoSettings || Campaign_SNS_LABEL[e.snsType]}
              </Typography>
            </Stack>

            <Controller
              key={index}
              name={`campaignSnsRequirements.${index}.requestUrl`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  aria-label="requestUrl"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      startAdornment: <InputAdornment position="end">@</InputAdornment>,
                    },
                  }}
                  placeholder="toridoribase"
                  error={!!error}
                  helperText={<ValidationErrorMessage error={error?.message} />}
                  fullWidth
                  {...field}
                  inputRef={ref}
                />
              )}
            />
          </Stack>
        ))
      })()}
    </CampaignInputComponent>
  )
}
